import { t } from '@lingui/core/macro';
import { OfferLinkedLocation, OfferLinkedLocationType, SolutionTag, Tag } from '@luminovo/api-contracts';
import { isPresent } from '@luminovo/commons';
import {
    colorSystem,
    CopyToClipboardTooltip,
    Flexbox,
    Link,
    StatusChip,
    StatusChipDefaultContainer,
    Text,
    Tooltip,
} from '@luminovo/design-system';
import { IconButton, styled } from '@mui/material';
import { CopyIcon } from 'lucide-react';
import type { JSX } from 'react';

import { formatOfferLinkedLocation, formatRegionAsName } from '../../formatters';
import { hasSolutionTag } from '../../model/hasSolutionTag';
import { InventorySiteChip } from './InventorySiteChip';

/** Needs no `SupplierAndStockLocationDTO` lookup; the supplier chip mirrors `SupplierAndStockLocationChip`. */
export function OfferLinkedLocationChip({
    location,
    isPreferred,
    isApproved,
    isExcluded,
    displaySiteName = true,
}: {
    location: OfferLinkedLocation;
    isPreferred: boolean;
    isApproved: boolean;
    isExcluded: boolean;
    displaySiteName?: boolean;
}): JSX.Element {
    if (location.type === OfferLinkedLocationType.InventorySite) {
        return (
            <InventorySiteChip
                site={location}
                isPreferred={isPreferred}
                isApproved={isApproved}
                isExcluded={isExcluded}
                displaySiteName={displaySiteName}
            />
        );
    }

    const statusColor = isPreferred ? 'primary' : isApproved ? 'green' : isExcluded ? 'red' : 'neutral';
    const statusLabel = isPreferred
        ? t`Preferred supplier`
        : isApproved
          ? t`Approved supplier`
          : isExcluded
            ? t`Excluded supplier`
            : t`Supplier not approved`;
    const locationName = isPresent(location.stock_location) ? formatRegionAsName(location.stock_location) : null;
    const shouldShowLocation = isPresent(locationName) && locationName.toLowerCase() !== 'unknown';

    return (
        <Tooltip
            variant="white"
            disableMaxWidth
            title={
                <Flexbox
                    gap={8}
                    style={{ padding: '8px' }}
                    sx={{
                        flexDirection: 'column',
                    }}
                >
                    <Flexbox
                        gap={8}
                        sx={{
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <span onClick={(e) => e.stopPropagation()}>
                            <Link
                                to="/suppliers/$supplierAndStockLocationId"
                                params={{ supplierAndStockLocationId: location.id }}
                                attention="high"
                                variant="body-semibold"
                                style={{ color: colorSystem.neutral[9] }}
                            >
                                {location.supplier.name}
                            </Link>
                        </span>
                        {shouldShowLocation && (
                            <Text variant="body-small" color={colorSystem.neutral[6]}>
                                {locationName}
                            </Text>
                        )}
                    </Flexbox>
                    {isPresent(location.number) && <SupplierNumberRow number={location.number} />}
                    <StatusChip color={statusColor} label={statusLabel} />
                </Flexbox>
            }
        >
            <span>
                <StatusChip
                    overrides={{ Container }}
                    color={isPreferred || isApproved ? 'green' : 'red'}
                    label={formatOfferLinkedLocation(location)}
                    enableHoverEffect={true}
                />
            </span>
        </Tooltip>
    );
}

export function OfferLinkedLocationChipFromSolutionTags({
    location,
    solutionTags,
    displaySiteName = true,
}: {
    location: OfferLinkedLocation;
    solutionTags: Tag[];
    displaySiteName?: boolean;
}): JSX.Element {
    const solution = { solutionTags };
    return (
        <OfferLinkedLocationChip
            location={location}
            isPreferred={hasSolutionTag(solution, SolutionTag.SupplierPreferred)}
            isApproved={hasSolutionTag(solution, SolutionTag.SupplierApproved)}
            isExcluded={hasSolutionTag(solution, SolutionTag.SupplierExcluded)}
            displaySiteName={displaySiteName}
        />
    );
}

function SupplierNumberRow({ number }: { number: string }): JSX.Element {
    return (
        <Flexbox
            gap={6}
            sx={{
                alignItems: 'center',
            }}
        >
            <Text variant="body-small" color={colorSystem.neutral[7]}>
                {number}
            </Text>
            <span
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <CopyToClipboardTooltip value={number} variant="white">
                    <IconButton
                        aria-label={t`Copy to Clipboard`}
                        size="small"
                        sx={{
                            padding: '2px',
                            color: colorSystem.neutral[6],
                            '&:hover': {
                                color: colorSystem.neutral[8],
                                backgroundColor: colorSystem.neutral[2],
                            },
                        }}
                    >
                        <CopyIcon size={14} />
                    </IconButton>
                </CopyToClipboardTooltip>
            </span>
        </Flexbox>
    );
}

const Container = styled(StatusChipDefaultContainer)({
    cursor: 'inherit',
    '& span': {
        maxWidth: 200,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    },
});
